import React, {useCallback, useEffect, useMemo} from 'react';
import {Box, CircularProgress, makeStyles} from '@material-ui/core';
import {Container, Label, Link, Row} from 'components-lib';
import {ModalVersions} from './ModalVersions';
import {FileSelectionLoadingProvider, useFileSelectionLoading, useModal} from 'hooks';
import {SingleFileUpload} from 'admin/resources/upload/components';
import {FormNameEnum} from 'enums/form';
import {useFormikContext} from 'formik';
import {IEditFileMetadataFormBaseValues} from 'admin/resources/files/views/edit/components/FilesEditResourceForm/config';
import {
  ALLOWED_MIMETYPE_FORMATS,
  audioMimeType,
  documentMimeTypes,
  excelMimeTypes,
  imageMimeTypes,
  pdfMimeType,
  videoMimeType,
  zipMimeType,
} from 'admin/resources';

export const VersionFileUpload = () => {
  const {values} = useFormikContext<IEditFileMetadataFormBaseValues>();
  const resourceMimeTypes = ALLOWED_MIMETYPE_FORMATS.join(', ');

  const allowedFormats = useMemo(() => {
    if (values?.fileTypeRecognition?.isVideo) {
      return videoMimeType;
    }

    if (values?.fileTypeRecognition?.isAudio) {
      return audioMimeType;
    }

    if (values?.fileTypeRecognition?.isImage) {
      return imageMimeTypes;
    }

    if (values?.fileTypeRecognition?.isDocument) {
      return documentMimeTypes;
    }

    if (values?.fileTypeRecognition?.isExcel) {
      return excelMimeTypes;
    }

    if (values?.fileTypeRecognition?.isPDF) {
      return pdfMimeType;
    }

    if (values?.fileTypeRecognition?.isArchive || values?.fileTypeRecognition?.isScormPackage) {
      return zipMimeType;
    }

    return resourceMimeTypes;
  }, [values.fileTypeRecognition, resourceMimeTypes]);

  return (
    <FileSelectionLoadingProvider name={FormNameEnum.file} accept={allowedFormats}>
      <VersionFileUploadInner allowedFormats={allowedFormats} />
    </FileSelectionLoadingProvider>
  );
};

interface IVersionFileUploadInnerProps {
  allowedFormats: string;
}

const VersionFileUploadInner = ({allowedFormats}: IVersionFileUploadInnerProps) => {
  const classes = useStyles();
  const {isOpen: isModalVersionsOpen, setIsOpen: setIsModalVersionsOpen} = useModal();
  const {values, setFieldValue} = useFormikContext<IEditFileMetadataFormBaseValues>();
  const fileSelectionLoading = useFileSelectionLoading();
  const isApplyingFile = !!fileSelectionLoading?.isApplyingFile;
  const shouldDisableReUploadButton = !values?.canEdit;

  const openModalVersionsClickHandler = useCallback(() => setIsModalVersionsOpen(true), [setIsModalVersionsOpen]);

  useEffect(() => {
    if (values?.file?.name) {
      setFieldValue(FormNameEnum.fileName, values.file.name, false);
    }
  }, [setFieldValue, values?.file]);

  return (
    <Container disableGutters>
      <Label label="Version" withLeftMargin={false} withFontBold />
      <Row justify="flex-start" align="flex-start">
        <Box pr={4}>
          {values?.fileVersion && (
            <Link clickHandler={openModalVersionsClickHandler} disabled={!values?.canEdit}>
              {values?.fileVersion}
            </Link>
          )}
        </Box>
        <Row justify="flex-start" align="flex-start" classes={{root: classes.fileUpload}}>
          <div className={classes.uploadSection}>
            <SingleFileUpload
              disableDragAndDrop
              disabled={shouldDisableReUploadButton || isApplyingFile}
              fullHeight={false}
              name={FormNameEnum.file}
              accept={allowedFormats}
              position="horizontal"
              align="flex-start"
              justify="flex-start"
              withOnlyBrowseButton
            />
            {isApplyingFile && (
              <div className={classes.loadingOverlay} aria-busy="true" aria-live="polite">
                <CircularProgress size={32} color="primary" />
              </div>
            )}
          </div>
        </Row>
      </Row>
      <ModalVersions isOpen={isModalVersionsOpen} setIsOpen={setIsModalVersionsOpen} versions={values?.versions} />
    </Container>
  );
};

const useStyles = makeStyles((theme) => ({
  fileUpload: {
    paddingTop: theme.spacing(3),
  },
  uploadSection: {
    position: 'relative',
    width: '100%',
  },
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    zIndex: 2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 48,
    backgroundColor: theme.palette.common.white,
  },
}));
